import { createFileRoute } from '@tanstack/react-router';
import { Download, Search, Upload } from 'lucide-react';
import { useState } from 'react';

import { APP_PAGE_GUTTER_X_CLASSNAME } from '@apps/web/components/layout/page_layout';
import { CsvImportModal } from '@apps/web/components/leaderboard/csv_import_modal';
import {
    type LeaderboardSortDirection,
    type LeaderboardSortKey,
    LeaderboardTable,
} from '@apps/web/components/leaderboard/leaderboard_table';
import { PointEntriesDrawer } from '@apps/web/components/leaderboard/point_entries_drawer';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { toast } from '@apps/web/lib/app_notifications';
import { isCaregiverPointsEnabled } from '@apps/web/lib/caregiver_points';
import { modeStore } from '@apps/web/lib/mode_store';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';
import { useLeaderboardPages } from '@apps/web/swr_data_fetching/caregiver_points_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';
import { type LeaderboardRowRead } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/(main)/leaderboard')({
    component: LeaderboardRoute,
    head: () => ({
        meta: [
            {
                title: 'Leaderboard | Phoebe',
            },
        ],
    }),
});

function LeaderboardRoute() {
    const organizationId = useOrganizationId();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 300);
    const [sortKey, setSortKey] = useState<LeaderboardSortKey>('total_points');
    const [sortDirection, setSortDirection] =
        useState<LeaderboardSortDirection>('desc');
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [selectedCaregiver, setSelectedCaregiver] =
        useState<LeaderboardRowRead | null>(null);
    const { data: organization, isLoading: isLoadingOrganization } =
        useOrganization(organizationId, {
            reason: 'leaderboard-route',
        });
    const caregiverPointsEnabled = isCaregiverPointsEnabled(
        organization?.features,
    );
    const {
        rows: leaderboardRows,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    } = useLeaderboardPages(
        caregiverPointsEnabled ? organizationId : null,
        { reason: 'leaderboard-route' },
        {
            limit: 200,
            search: debouncedSearch,
            sortBy: sortKey,
            sortDirection,
        },
    );

    const handleSortChange = (
        nextSortKey: LeaderboardSortKey,
        nextSortDirection: LeaderboardSortDirection,
    ) => {
        setSortKey(nextSortKey);
        setSortDirection(nextSortDirection);
    };

    const handleExport = async () => {
        if (!organizationId) {
            return;
        }
        try {
            const runtimeConfig = getRuntimeConfig();
            const mode = modeStore.getMode();
            const response = await fetch(
                `${runtimeConfig.apiUrl}/organizations/${organizationId}/caregiver_points/csv_export`,
                {
                    credentials: 'include',
                    headers: {
                        ...(mode ? { 'X-Mode': mode } : {}),
                        'X-Source': 'leaderboard-csv-export',
                    },
                },
            );
            if (!response.ok) {
                throw new Error(`Export failed with ${response.status}`);
            }
            const blob = await response.blob();
            const disposition = response.headers.get('Content-Disposition');
            const filenameMatch = disposition?.match(/filename="([^"]+)"/);
            const filename = filenameMatch?.[1] ?? 'phoebe-leaderboard.csv';
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('CSV export failed', {
                description: 'Please try again.',
                persistNotification: false,
            });
        }
    };

    if (!isLoadingOrganization && !caregiverPointsEnabled) {
        return (
            <main className="bg-background flex h-full min-h-0 flex-col">
                <div
                    className={cn(APP_PAGE_GUTTER_X_CLASSNAME, 'border-b py-4')}
                >
                    <h1 className="text-foreground text-xl font-semibold">
                        Leaderboard
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Caregiver points are not enabled for this organization.
                    </p>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-background flex h-full min-h-0 flex-col">
            <div className={cn(APP_PAGE_GUTTER_X_CLASSNAME, 'border-b py-4')}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h1 className="text-foreground text-xl font-semibold max-md:sr-only">
                            Leaderboard
                        </h1>
                        <p className="text-muted-foreground mt-1 text-sm">
                            Caregiver point totals from shift activity and
                            adjustments.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setIsImportOpen(true)}
                        >
                            <Upload className="size-4" />
                            Import CSV
                        </Button>
                        <Button variant="outline" onClick={handleExport}>
                            <Download className="size-4" />
                            Export CSV
                        </Button>
                    </div>
                </div>
                <div className="relative mt-4 max-w-md">
                    <Search className="text-muted-foreground/70 pointer-events-none absolute top-2.5 left-3 size-4" />
                    <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search caregivers"
                        className="pl-9"
                    />
                </div>
            </div>
            <LeaderboardTable
                rows={leaderboardRows}
                isLoading={isLoadingOrganization || isLoading}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onLoadMore={loadMore}
                onSortChange={handleSortChange}
                onSelectCaregiver={setSelectedCaregiver}
                timezone={organization?.timezone ?? 'UTC'}
            />
            <CsvImportModal
                organizationId={organizationId}
                open={isImportOpen}
                onOpenChange={setIsImportOpen}
                onImported={() => {
                    void mutate();
                }}
            />
            <PointEntriesDrawer
                organizationId={organizationId}
                caregiver={selectedCaregiver}
                timezone={organization?.timezone ?? 'UTC'}
                onOpenChange={(open) => {
                    if (!open) {
                        setSelectedCaregiver(null);
                    }
                }}
                onAdjusted={() => {
                    void mutate();
                }}
            />
        </main>
    );
}
